import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import Accommodations from "@/components/Accommodations";
import Events from "@/components/Events";
import Services from "@/components/Services";
import Reviews from "@/components/Reviews";
import BookingForm from "@/components/BookingForm";
import Footer from "@/components/Footer";
import Gallery from "@/components/Gallery";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import AdminLogin from "@/components/AdminLogin";
import AdminResetPassword from "@/pages/AdminResetPassword";
import AdminDashboard from "@/components/AdminDashboard";
import AdminMedia from "@/components/AdminMedia";
import AdminSettings from "@/components/AdminSettings";
import AdminPricing from "@/components/AdminPricing";
import AdminReviews from "@/components/AdminReviews";
import AdminAccounting from "@/components/AdminAccounting";
import PaymentSuccess from "@/components/PaymentSuccess";
import PaymentBalance from "@/components/PaymentBalance";
import PaymentCancelled from "@/components/PaymentCancelled";
import LegalPage from "@/pages/LegalPage";
import AdminLegal from "@/pages/AdminLegal";
import ReviewsPage from "@/pages/ReviewsPage";
import CaterersPage from "@/pages/CaterersPage";
import DjsPage from "@/pages/DjsPage";
import KitchenPage from "@/pages/KitchenPage";
import AdminCaterers from "@/components/AdminCaterers";
import AdminDjs from "@/components/AdminDjs";
import AdminKitchen from "@/components/AdminKitchen";
import AdminRefunds from "@/components/AdminRefunds";
import AdminCautions from "@/components/AdminCautions";
import AdminExtras from "@/components/AdminExtras";
import AdminIcalSync from "@/components/AdminIcalSync";
import SitePulseTicker from "@/components/SitePulseTicker";
import RouteTracker from "@/components/RouteTracker";
import DomainStats from "@/components/DomainStats";
import RecentBookingsNotifier from "@/components/RecentBookingsNotifier";
import Seo from "@/components/Seo";

const SEO_PAGES = {
  home: {
    title: "Le Domaine du Pilat — Gîte & Salle de réception dans le Parc du Pilat",
    description: "Gîte de charme, appartements et salle de réception au cœur du Parc naturel régional du Pilat, à 30 min de Saint-Étienne et 1h de Lyon. Devis instantané et réservation en ligne pour vos séjours, mariages et événements.",
    path: "/",
  },
  avis: {
    title: "Avis clients — Le Domaine du Pilat",
    description: "Découvrez les avis de nos hôtes sur le gîte, les appartements et la salle de réception du Domaine du Pilat. Note Google et témoignages vérifiés.",
    path: "/avis",
  },
  traiteurs: {
    title: "Traiteurs partenaires — Le Domaine du Pilat",
    description: "Nos traiteurs partenaires de confiance pour vos mariages, anniversaires et réceptions au Domaine du Pilat : cuisine locale et menus sur mesure.",
    path: "/traiteurs-partenaires",
  },
  djs: {
    title: "DJ & animation — Le Domaine du Pilat",
    description: "DJ et animateurs partenaires pour faire vibrer vos mariages et soirées dans la salle de réception du Domaine du Pilat.",
    path: "/dj-partenaires",
  },
  cuisine: {
    title: "Cuisine professionnelle — Le Domaine du Pilat",
    description: "Une cuisine professionnelle équipée à disposition de votre traiteur pour vos réceptions au Domaine du Pilat.",
    path: "/cuisine-professionnelle",
  },
  cgv: {
    title: "Conditions générales de vente — Le Domaine du Pilat",
    description: "Conditions générales de vente du Domaine du Pilat : réservation, acompte, annulation et taxe de séjour.",
    path: "/cgv",
  },
  "mentions-legales": {
    title: "Mentions légales — Le Domaine du Pilat",
    description: "Mentions légales du site ledomainedupilat.fr.",
    path: "/mentions-legales",
  },
  confidentialite: {
    title: "Politique de confidentialité — Le Domaine du Pilat",
    description: "Politique de confidentialité et protection des données personnelles du Domaine du Pilat.",
    path: "/confidentialite",
  },
};

function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Seo {...SEO_PAGES.home} />
      <SitePulseTicker />
      <Navigation />
      <Hero />
      <Accommodations />
      <Events />
      <Gallery />
      <Services />
      <Reviews />
      <DomainStats />
      <AvailabilityCalendar />
      <BookingForm />
      <Footer />
      <RecentBookingsNotifier />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <RouteTracker />
      <Toaster position="top-center" richColors closeButton />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/reset-password" element={<AdminResetPassword />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/medias" element={<AdminMedia />} />
        <Route path="/admin/parametres" element={<AdminSettings />} />
        <Route path="/admin/tarifs" element={<AdminPricing />} />
        <Route path="/admin/avis" element={<AdminReviews />} />
        <Route path="/admin/comptabilite" element={<AdminAccounting />} />
        <Route path="/paiement/success" element={<PaymentSuccess />} />
        <Route path="/paiement/solde/:reservationId" element={<PaymentBalance />} />
        <Route path="/paiement/annule" element={<PaymentCancelled />} />
        <Route path="/cgv" element={<><Seo {...SEO_PAGES.cgv} /><LegalPage slug="cgv" /></>} />
        <Route path="/mentions-legales" element={<><Seo {...SEO_PAGES["mentions-legales"]} /><LegalPage slug="mentions-legales" /></>} />
        <Route path="/confidentialite" element={<><Seo {...SEO_PAGES.confidentialite} /><LegalPage slug="confidentialite" /></>} />
        <Route path="/admin/pages-legales" element={<AdminLegal />} />
        <Route path="/avis" element={<><Seo {...SEO_PAGES.avis} /><ReviewsPage /></>} />
        <Route path="/traiteurs-partenaires" element={<><Seo {...SEO_PAGES.traiteurs} /><CaterersPage /></>} />
        <Route path="/dj-partenaires" element={<><Seo {...SEO_PAGES.djs} /><DjsPage /></>} />
        <Route path="/cuisine-professionnelle" element={<><Seo {...SEO_PAGES.cuisine} /><KitchenPage /></>} />
        <Route path="/admin/traiteurs" element={<AdminCaterers />} />
        <Route path="/admin/djs" element={<AdminDjs />} />
        <Route path="/admin/cuisine" element={<AdminKitchen />} />
        <Route path="/admin/remboursements" element={<AdminRefunds />} />
        <Route path="/admin/cautions" element={<AdminCautions />} />
        <Route path="/admin/extras" element={<AdminExtras />} />
        <Route path="/admin/ical" element={<AdminIcalSync />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
